import React, { useState } from 'react';
import Moment from 'react-moment';
import styled from 'styled-components';

import { useEventTypes, useExternalReviewEnabledForEvent, useTimezone } from '../../hooks';
import { useHasAccess } from '../../hooks/useHasAccess';
import { Event } from '../../interfaces';
import { basic } from '../../theme/colors';
import { VehicleName, getTimeOffset, humanizeEnum, isAggregableEvent } from '../../utils';
import { getSentToProcessorStatus } from '../../utils/sentToProcessorStatus';
import { Centered, CopyToClipboard, FlatTable, InternalOnly, Modal, Padded, Time } from '../atoms';
import { EventVideo } from './EventVideo';
import { Geocode } from './Geocode';

interface Props {
  event: Event;
}

export function EventDetails(props: Props) {
  const { event } = props;
  const timezone = useTimezone();
  const [secondEventPreview, setSecondEventPreview] = useState<Event>(null);
  const externaReviewEnabled = useExternalReviewEnabledForEvent(event);

  const eventTypes = useEventTypes();

  const hasAccess = useHasAccess();

  const openPreview = () => setSecondEventPreview(props.event.sub_events[1]);
  const closeModal = () => setSecondEventPreview(null);

  return (
    <>
      <FlatTable borderless size="sm">
        <tbody>
          {isAggregableEvent(event) && event.sub_events ? (
            <>
              <Tr>
                <th colSpan={2}>First Observation</th>
              </Tr>

              <Tr>
                <td>Observation Time</td>
                <td>
                  <Time>{event.sub_events[0].captured_at}</Time>
                </td>
              </Tr>

              <Tr>
                <td>Observation Date</td>
                <td>
                  <Moment tz={timezone} format="MMM D, YYYY">
                    {event.sub_events[0].captured_at}
                  </Moment>
                </td>
              </Tr>

              <Tr>
                <td>Bus Route</td>
                <td>{event.route_name}</td>
              </Tr>

              <Tr>
                <td>Bus Number</td>
                <td>
                  <VehicleName event={event} />
                </td>
              </Tr>

              <Tr>
                <th colSpan={2}>
                  Second Observation
                  <span onClick={openPreview}>Preview ▹</span>
                </th>
              </Tr>

              <Tr>
                <td>Observation Time</td>
                <td>
                  <Time>{event.sub_events[1].captured_at}</Time>
                  <small> ({getTimeOffset(event.sub_events[0], event.sub_events[1])})</small>
                </td>
              </Tr>

              <Tr>
                <td>Bus Route</td>
                <td>{event.route_name}</td>
              </Tr>

              <Tr>
                <td>Bus Number</td>
                <td>
                  <VehicleName event={event} />
                </td>
              </Tr>
            </>
          ) : (
            <>
              <tr>
                <td>Observation Time</td>
                <td>
                  <Time>{event.captured_at}</Time>
                </td>
              </tr>

              <tr>
                <td>Observation Date</td>
                <td>
                  <Moment tz={timezone} format="MMM D, YYYY">
                    {event.captured_at}
                  </Moment>
                </td>
              </tr>
              <tr>
                <td>Bus Route</td>
                <td>{event.route_name}</td>
              </tr>

              <tr>
                <td>Bus Number</td>
                <td>
                  <VehicleName event={event} />
                </td>
              </tr>
            </>
          )}

          <tr>
            <td>Location</td>
            <td>
              <Geocode event={event} />
            </td>
          </tr>

          <tr>
            <td>Observation Type</td>
            <td>{eventTypes?.find((et) => et.identifier === event.event_type_identifier)?.name}</td>
          </tr>

          <tr>
            <td>Vehicle Type</td>
            <td style={{ textTransform: 'capitalize' }}>{event.vehicle_type ?? 'N/A'}</td>
          </tr>
          {hasAccess && (
            <tr>
              <td>Enforcement Period</td>
              {/* We default to "true" for events that don't have information about this */}
              <td>{event.within_enforce_time === false ? 'No' : 'Yes'}</td>
            </tr>
          )}

          {event.metadata?.route_metadata?.position && (
            <tr>
              <td>Position</td>
              <td style={{ textTransform: 'capitalize' }}>{event.metadata?.route_metadata?.position}</td>
            </tr>
          )}

          {event?.evidence_pkg_url && (
            <tr>
              <td>Evidence Package</td>
              <td>
                <a href={event.evidence_pkg_url}>Download</a>
              </td>
            </tr>
          )}

          <tr>
            <td>Sent to processor</td>
            <td>{getSentToProcessorStatus(event)}</td>
          </tr>

          {externaReviewEnabled && (
            <tr>
              <td>EXternal review</td>
              <td>{humanizeEnum(event.status) ?? 'Unprocessed'}</td>
            </tr>
          )}

          <InternalOnly>
            <tr>
              <td>Device ID</td>
              <td>
                <CopyToClipboard>{event.device_id}</CopyToClipboard>
              </td>
            </tr>
          </InternalOnly>
        </tbody>
      </FlatTable>

      {secondEventPreview && (
        <Modal isOpen onToggle={closeModal} contentLabel="Second Observation Preview">
          <Padded>
            <Centered>
              <EventVideo event={secondEventPreview} />
            </Centered>
          </Padded>
        </Modal>
      )}
    </>
  );
}

const Tr = styled.tr`
  th {
    padding: 1em 0 1em 0em;

    span {
      font-size: 14px;
      letter-spacing: -0.6px;
      color: ${basic.haydenBrand};
      cursor: pointer;
      float: right;
    }
  }

  &:first-of-type th {
    padding-top: 0;
  }

  td {
    padding-left: 1em;

    &:first-of-type {
      border-left: 3px solid #eee;
    }
  }

  &:last-of-type {
    margin-bottom: 1em;
  }
`;
